import { useEffect, type ReactNode } from "react";
import { Nav } from "@/components/Nav";
import styles from "./privacy.module.css";

const PAGE_TITLE = "HireOS Support";

/**
 * Where a person who uses HireOS gets help — the support URL the App Store
 * listing points at. Laid out like the privacy page, whose stylesheet it
 * shares rather than copies, and like it says only what is true today: the
 * password reset is the admin's own (`/forgot-password`, a link that works
 * for an hour), and deleting a login is in the app's Settings.
 */
export default function SupportPage() {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  return (
    <div className={styles.page}>
      <Nav />

      <main className={styles.main}>
        <div className={styles.prose}>
          <h1 className={styles.title}>Support</h1>

          <Section title="What HireOS is">
            <p>
              The phone line and enquiry inbox for hire and rental companies:
              every call, text and form enquiry, in one place.
            </p>
          </Section>

          <Section title="Getting help">
            <p>
              Email{" "}
              <a href="mailto:hello@loony.io" className={styles.link}>
                hello@loony.io
              </a>{" "}
              and a person will get back to you. Tell us your company's name
              and what you were trying to do.
            </p>
          </Section>

          <Section title="Signing in">
            <p>
              Sign in with the email and password your company set you up
              with, in the app or at{" "}
              <a href="https://admin.hireos.co.nz" className={styles.link}>
                admin.hireos.co.nz
              </a>
              .
            </p>
            <p className={styles.followingParagraph}>
              Forgotten your password? Tap "Forgot password?" on the sign-in
              screen and enter your email. We'll send you a link to set a new
              one, and it works for one hour. If it doesn't arrive, check your
              spam folder, or ask whoever runs HireOS at your company to invite
              you again.
            </p>
          </Section>

          <Section title="Deleting your login">
            <p>
              Delete your login from Settings in the app, or email{" "}
              <a href="mailto:hello@loony.io" className={styles.link}>
                hello@loony.io
              </a>{" "}
              and we'll do it for you. Your login goes straight away. The
              enquiries and calls belong to your company and stay with it. See
              the{" "}
              <a href="/privacy" className={styles.link}>
                privacy policy
              </a>{" "}
              for what we hold.
            </p>
          </Section>

          <Section title="Who runs HireOS">
            <p>
              HireOS is run by HireOS Limited, a New Zealand company.
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>{title}</h2>
      {children}
    </section>
  );
}
